// src/pages/Feed.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';

const Feed = () => {
  const [dogs, setDogs] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  const navigate = useNavigate();

  // Retrieve token and decode user info (if logged in)
  const token = localStorage.getItem('token');
  let user = null;
  if (token) {
    try {
      user = jwtDecode(token);
    } catch (error) {
      console.error('Invalid token');
    }
  }

  // Fetch dog posts when the component mounts
  useEffect(() => {
    fetchDogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDogs = async (pageNum) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dogs?page=${pageNum}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.dogs && data.dogs.length > 0) {
        const newDogs = data.dogs.map((dog) => ({
          ...dog,
          // Use the hearted flag from the response.
          hearted: dog.hearted,
        }));
        setDogs((prevDogs) => {
          const seenIds = new Set(prevDogs.map((dog) => dog.id));
          const filteredNewDogs = newDogs.filter((dog) => !seenIds.has(dog.id));
          return [...prevDogs, ...filteredNewDogs];
        });
        if (data.dogs.length < 10) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error fetching dogs:', err);
    }
    setLoading(false);
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchDogs(nextPage);
  };

  const toggleHeart = async (dogId, currentStatus) => {
    if (!user) {
      toast.error("Please log in to heart a dog post.");
      navigate('/login');
      return;
    }
    
    if (currentStatus) {
      // Unheart the post
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/dogs/${dogId}/heart?user_id=${user.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.ok) {
          setDogs(prevDogs =>
            prevDogs.map(dog =>
              dog.id === dogId ? { ...dog, hearted: false, hearts: dog.hearts - 1 } : dog
            )
          );
        } else {
          const errData = await response.json();
          toast.error(errData.error || "Error removing heart");
        }
      } catch (err) {
        console.error("Error removing heart:", err);
        toast.error("Error removing heart");
      }
    } else {
      // Heart the post
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/dogs/${dogId}/heart`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ user_id: user.id }),
        });
        if (response.ok) {
          setDogs(prevDogs =>
            prevDogs.map(dog =>
              dog.id === dogId ? { ...dog, hearted: true, hearts: dog.hearts + 1 } : dog
            )
          );
        } else {
          const errData = await response.json();
          if (errData.error && errData.error.includes("already hearted")) {
            setDogs(prevDogs =>
              prevDogs.map(dog =>
                dog.id === dogId ? { ...dog, hearted: true } : dog
              )
            );
          } else {
            toast.error(errData.error || "Error adding heart");
          }
        }
      } catch (err) {
        console.error("Error adding heart:", err);
        toast.error("Error adding heart");
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-2">
      {/* Prominent CTA Banner */}
      <div className="mb-8 bg-gradient-to-r from-green-400 to-blue-500 rounded-lg p-6 text-center text-white shadow-lg">
        <h2 className="text-3xl sm:text-4xl font-bold mb-2">Got an Amazing Dog?</h2>
        <p className="text-lg sm:text-xl mb-4">Share your dog's story and make them viral!</p>
        <button
          onClick={() => navigate('/submit')}
          className="bg-white text-green-600 font-bold py-2 px-4 sm:py-3 sm:px-6 rounded-full hover:bg-gray-100 transition"
        >
          Submit Your Dog
        </button>
      </div>
      
      <h1 className="text-2xl sm:text-3xl font-bold mb-4">Dog Feed</h1>
      
{/* Grid of Dog Posts */}
<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {dogs.map((dog) => {
          const isUserPost = user && dog.user_id === user.id;
          return (
            <div
              key={dog.id}
              className={`bg-white p-4 shadow rounded ${isUserPost ? 'border-4 border-green-500' : ''}`}
            >
              {isUserPost && (
                <div className="mb-2">
                  <span className="bg-green-500 text-white text-xs font-bold uppercase px-2 py-1 rounded">
                    Your Post
                  </span>
                </div>
              )}
              {dog.image_url && (
                // Wrap the image in a clickable container
                <div
                  className="w-full max-h-[17rem] overflow-hidden rounded flex items-center justify-center cursor-pointer"
                  onClick={() => setSelectedImage(dog.image_url)}
                >
                  <img
                    src={dog.image_url}
                    alt="Dog"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <p className="mt-2 mb-1">{dog.story}</p>
              <p className="text-sm text-gray-500 mb-1">Hashtags: {dog.hashtags}</p>
              <div className="flex items-center space-x-2">
                <span className="text-sm">Hearts: {dog.hearts}</span>
                {user && (
                  <button
                    onClick={() => toggleHeart(dog.id, dog.hearted)}
                    className="text-2xl focus:outline-none"
                  >
                    {dog.hearted ? (
                      <span style={{ color: 'red' }}>♥</span>
                    ) : (
                      <span style={{ color: 'gray' }}>♥</span>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="text-center my-4">
          <button
            onClick={loadMore}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
            disabled={loading}
          >
            {loading ? "Loading..." : "Load More"}
          </button>
        </div>
      )}

      {/* Full-Screen Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-75 cursor-pointer"
          onClick={() => setSelectedImage(null)}
        >
          <img
            src={selectedImage}
            alt="Full Screen Dog"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default Feed;
